import { Component } from "react"
import PropTypes from "prop-types"

import isFeatureEnabled from "shared/featureflags/helperFunctions"

import genericFilterGroup from "shared/search/containers/FilterGroup/FilterGroups.js"
import { createCustomFilterGroup } from "shared/search/containers/FilterGroup/CustomFilterGroup"

import {
    createListFilter,
    createNotInListFilter,
    ManuallyChangedInListFilter,
    RepresentingStateFilter,
    RepresentingCountyFilter,
    RepresentingMunicipalityFilter,
    PopulationStratumFilter,
} from "shared/search/containers/Filter/Filters"
import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import {
    StafferCaucusFilter,
    StafferChamberFilter,
    StafferCommitteeFilter,
    StafferEmailFilter,
    StafferExecutiveFilter,
    StafferIssuesFilter,
    StafferLegislatorFilter,
    StafferOfficeLocationFilter,
    StafferPartyFilter,
    StafferProjectFilter,
    StafferRepresentingAddressFilter,
    StafferRolesFilter,
    StafferFromBulkUploadFilter,
    StafferTypeFilter,
    StafferMajorityMinorityFilter,
    StafferCustomEventFilter,
    StafferInvitedToEventCustomEventFilter,
    StafferHasNotRsvpCustomEventFilter,
    StafferHasRsvpYesCustomEventFilter,
    StafferHasRsvpNoCustomEventFilter,
    StafferHasRsvpMaybeCustomEventFilter,
    StafferAttendedCustomEventFilter,
    // committee profile
    StafferCommitteeProfileFilter,
} from "shared/search/containers/Filter/StafferFilters"

import {
    PersonTitleFilter,
    PersonStatusFilter,
    PersonRepresentingCustomGeographyFilter,
    PersonRepresentingCountryAllFilter,
    PersonRepresentingCountryEUFilter,
} from "shared/search/containers/Filter/OfficialFilters"

import { bulkUploadIsAvailable } from "shared/search/helperFunctions"

import { notInternationalEURegions } from "shared/imports/regionConstants"

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

export const StafferCustomFilterGroup = createCustomFilterGroup(
    "stafferCustomFilterGroup",
    DjangIO.app.models.QuorumDataType.staffer.value,
)

export class StafferTypeFilterGroup extends Component {
    static defaultProps = {
        filters: [
            StafferTypeFilter,
            PersonStatusFilter,
            StafferOfficeLocationFilter,
            StafferChamberFilter,
            StafferPartyFilter,
            StafferMajorityMinorityFilter,
            StafferLegislatorFilter,
            StafferExecutiveFilter,
            StafferCommitteeFilter,
            StafferCaucusFilter,
            StafferRolesFilter,
            StafferIssuesFilter,
            PersonTitleFilter,
            StafferEmailFilter,
        ],
        uniqueFilterGroupId: "stafferTypeFilterGroup",
        displayLabel: "Staffer Type",
        helpText: "Filter staffers by office, party, who they work for, and more.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

// for use in Official Profiles
export class StafferInformationFilterGroup extends Component {
    static defaultProps = {
        filters: [
            StafferTypeFilter,
            PersonStatusFilter,
            StafferOfficeLocationFilter,
            StafferRolesFilter,
            StafferIssuesFilter,
            PersonTitleFilter,
            StafferEmailFilter,
        ],
        uniqueFilterGroupId: "stafferInformationFilterGroup",
        displayLabel: "Staffer Information",
        helpText: "Filter staffers by type, office, titles, and issues.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class StafferListFilterGroup extends Component {
    static defaultProps = {
        filters: [
            createListFilter(DjangIO.app.models.QuorumDataType.staffer.value),
            createNotInListFilter(DjangIO.app.models.QuorumDataType.staffer.value),
            ManuallyChangedInListFilter,
        ],
        uniqueFilterGroupId: "stafferListFilterGroup",
        displayLabel: "Lists",
        helpText: "Filter results by the lists that you have saved them to in Projects.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class StafferDataMaintenanceFilterGroup extends Component {
    static defaultProps = {
        filters: bulkUploadIsAvailable(DjangIO.app.custom_data.types.BulkUploadType.staffer)
            ? [StafferFromBulkUploadFilter]
            : [],
        uniqueFilterGroupId: "stafferDataMaintenanceFilterGroup",
        displayLabel: "Data Maintenance",
        helpText: "Filter staffers by source.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const StafferProjectFilterGroup = (props) => genericFilterGroup(props)
StafferProjectFilterGroup.propTypes = defaultProps
StafferProjectFilterGroup.defaultProps = {
    filters: [inIssueFilter, notInIssueFilter],
    uniqueFilterGroupId: "stafferProjectFilterGroup",
    displayLabel: "Issues",
    helpText: "Filter results by the issue they are tagged to.",
    dataCy: "staffer-issue-filter-group",
}

export const StafferCustomEventFilterGroup = (props) => genericFilterGroup(props)
StafferCustomEventFilterGroup.propTypes = defaultProps
StafferCustomEventFilterGroup.defaultProps = {
    filters: [
        StafferCustomEventFilter,
        StafferInvitedToEventCustomEventFilter,
        StafferHasNotRsvpCustomEventFilter,
        StafferHasRsvpYesCustomEventFilter,
        StafferHasRsvpNoCustomEventFilter,
        StafferHasRsvpMaybeCustomEventFilter,
        StafferAttendedCustomEventFilter,
    ],
    permissions: [DjangIO.app.models.PermissionType.custom_events],
    uniqueFilterGroupId: "stafferCustomEventFilterGroup",
    displayLabel: "Events",
    helpText: "Filter staffers by the event that they are participating in.",
}

export const StafferCommitteeProfileFilterGroup = (props) => genericFilterGroup(props)
StafferCommitteeProfileFilterGroup.propTypes = defaultProps
StafferCommitteeProfileFilterGroup.defaultProps = {
    filters: [StafferCommitteeProfileFilter],
    uniqueFilterGroupId: "stafferCommitteeProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export class StafferRepresentsRegionFilterGroup extends Component {
    static defaultProps = {
        filters: [
            StafferRepresentingAddressFilter,
            RepresentingStateFilter,
            RepresentingCountyFilter,
            RepresentingMunicipalityFilter,
            PopulationStratumFilter,
            PersonRepresentingCustomGeographyFilter,
            PersonRepresentingCountryAllFilter,
            PersonRepresentingCountryEUFilter,
        ],
        uniqueFilterGroupId: "stafferRepresentsRegionFilterGroup",
        displayLabel: "Region",
        regions: notInternationalEURegions,
        helpText:
            "Filter by the geographical areas people " +
            "represent or by a particular address. If you choose a given region " +
            "(for example, New York City), we will return all whose " +
            "districts at all overlap that region.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}
